import React from 'react';
import { Box, Paper, Typography, Button, IconButton, Fade, useTheme, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Notification } from '../../types/notifications';
import { getIconByType } from '../Navigation/NotificationPanel';

interface NotificationAlertProps {
  notification: Notification;
  onClose: () => void;
  onMarkAsRead: () => void;
  onSeeMore: () => void;
}

export const NotificationAlert: React.FC<NotificationAlertProps> = ({
  notification,
  onClose,
  onMarkAsRead,
  onSeeMore,
}) => {
  const theme = useTheme();
  const primaryColor = '#FF4405'; // Ana's primary orange color

  return (
    <Fade in={true}>
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          width: '380px',
          borderRadius: '12px',
          overflow: 'hidden',
          zIndex: theme.zIndex.snackbar,
          backgroundColor: theme.palette.mode === 'dark' ? '#242424' : '#FFFFFF',
          border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E5E7EB'}`,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 0 0 1px rgba(255, 255, 255, 0.12), 0 8px 20px rgba(0, 0, 0, 0.7), 0 16px 48px rgba(0, 0, 0, 0.4)'
            : '0 0 0 1px rgba(0, 0, 0, 0.05), 0 8px 20px rgba(0, 0, 0, 0.1), 0 16px 48px rgba(0, 0, 0, 0.08)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: primaryColor,
          }
        }}
      >
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme.palette.mode === 'dark' 
                    ? `linear-gradient(135deg, ${primaryColor}25 0%, ${primaryColor}40 100%)`
                    : `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}30 100%)`,
                }}
              >
                {React.cloneElement(getIconByType(notification), {
                  sx: { width: 24, height: 24, color: primaryColor }
                })}
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#111827',
                fontSize: '1rem',
              }}>
                {notification.scan_type?.toUpperCase()}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Message */}
          <Tooltip
            title={notification.message.length > 100 ? notification.message : ''}
            placement="top"
          >
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.9)',
                mb: 3,
                lineHeight: 1.5,
                maxWidth: '100%'
              }}
            >
              {notification.message.length > 100 ? `${notification.message.substring(0, 100)}...` : notification.message}
            </Typography>
          </Tooltip>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button
              size="medium"
              variant="text"
              onClick={onMarkAsRead}
              sx={{
                color: theme.palette.mode === 'dark' ? `${primaryColor}` : primaryColor,
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? `${primaryColor}20` : `${primaryColor}15`,
                }
              }}
            >
              Mark as Read
            </Button>
            {notification.scan_type?.toLowerCase() !== 'checkmarx' && 
             notification.scan_type?.toLowerCase() !== 'wiz' && (
              <Button
                size="medium"
                variant="contained"
                onClick={onSeeMore}
                sx={{
                  backgroundColor: primaryColor,
                  color: 'white',
                  fontWeight: 500,
                  boxShadow: `0 2px 4px ${primaryColor}40`,
                  '&:hover': {
                    backgroundColor: '#E63D04',
                    boxShadow: `0 4px 8px ${primaryColor}60`,
                  }
                }}
              >
                See More
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};